import { useState, useMemo } from 'react';
import { LoginLogEntry } from '../../../../hooks/useAccessLogs';

interface Props {
  loginLog: LoginLogEntry[];
}

const PAGE_SIZE = 50;

const DEVICE_ICONS: Record<string, string> = {
  desktop: 'ri-computer-line',
  mobile: 'ri-smartphone-line',
  tablet: 'ri-tablet-line',
};

const DEVICE_COLORS: Record<string, string> = {
  desktop: 'text-cyan-400',
  mobile: 'text-violet-400',
  tablet: 'text-amber-400',
};

const BROWSER_ICONS: Record<string, string> = {
  chrome: 'ri-chrome-line',
  firefox: 'ri-firefox-line',
  safari: 'ri-safari-line',
  edge: 'ri-edge-line',
};

const getBrowserIcon = (browser: string | null): string => {
  if (!browser) return 'ri-global-line';
  const b = browser.toLowerCase();
  if (b.includes('chrome')) return BROWSER_ICONS.chrome;
  if (b.includes('firefox')) return BROWSER_ICONS.firefox;
  if (b.includes('safari')) return BROWSER_ICONS.safari;
  if (b.includes('edge')) return BROWSER_ICONS.edge;
  return 'ri-global-line';
};

const fmtDateTime = (d: string | null): { date: string; time: string } => {
  if (!d) return { date: '—', time: '' };
  const dt = new Date(d);
  return {
    date: dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
};

const getDayLabel = (d: string | null): string => {
  if (!d) return 'Data desconhecida';
  const dt = new Date(d);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const dStr = dt.toISOString().slice(0, 10);
  const tStr = today.toISOString().slice(0, 10);
  const yStr = yesterday.toISOString().slice(0, 10);
  if (dStr === tStr) return 'Hoje';
  if (dStr === yStr) return 'Ontem';
  return dt.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

type QuickFilter = 'today' | '7d' | '30d' | 'all';

const QUICK_FILTERS: { id: QuickFilter; label: string }[] = [
  { id: 'today', label: 'Hoje' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: '30d', label: 'Últimos 30 dias' },
  { id: 'all', label: 'Todo o período' },
];

const getQuickStart = (qf: QuickFilter): Date | null => {
  if (qf === 'all') return null;
  const now = new Date();
  if (qf === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (qf === '7d') return new Date(now.getTime() - 7 * 86400000);
  return new Date(now.getTime() - 30 * 86400000);
};

// Checks if a session falls within the period, using EITHER login_at OR last_active_at.
// This way, a session created yesterday but still active today appears under "Hoje".
const isSessionInPeriod = (row: LoginLogEntry, start: Date): boolean => {
  const loginDate = row.login_at ? new Date(row.login_at) : null;
  const lastActive = row.last_active_at ? new Date(row.last_active_at) : null;
  return (loginDate !== null && loginDate >= start) || (lastActive !== null && lastActive >= start);
};

const avatarColor = (name: string): string => {
  const colors = [
    'bg-violet-600/30 text-violet-300',
    'bg-cyan-600/30 text-cyan-300',
    'bg-emerald-600/30 text-emerald-300',
    'bg-pink-600/30 text-pink-300',
    'bg-amber-600/30 text-amber-300',
    'bg-indigo-600/30 text-indigo-300',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

export const LoginLogTable = ({ loginLog }: Props) => {
  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [browserFilter, setBrowserFilter] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let rows = [...loginLog];

    // quick date filter — uses last_active_at OR login_at so sessions active today appear under "Hoje"
    const start = getQuickStart(quickFilter);
    if (start) rows = rows.filter((r) => isSessionInPeriod(r, start));

    // search
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) => r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
      );
    }

    // device
    if (deviceFilter) rows = rows.filter((r) => (r.device_type ?? '') === deviceFilter);

    // browser
    if (browserFilter) {
      rows = rows.filter((r) =>
        r.browser_name?.toLowerCase().includes(browserFilter.toLowerCase()),
      );
    }

    return rows;
  }, [loginLog, search, quickFilter, deviceFilter, browserFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetPage = () => setPage(1);

  // Unique browsers for filter
  const browsers = useMemo(() => {
    const seen = new Set<string>();
    loginLog.forEach((r) => {
      if (r.browser_name) seen.add(r.browser_name);
    });
    return Array.from(seen).sort();
  }, [loginLog]);

  // Group rows by day for visual separation
  const groupedRows = useMemo(() => {
    const groups: { dayLabel: string; dayKey: string; rows: LoginLogEntry[] }[] = [];
    let currentDay = '';
    paginated.forEach((r) => {
      const dayKey = r.login_at ? r.login_at.slice(0, 10) : 'unknown';
      if (dayKey !== currentDay) {
        currentDay = dayKey;
        groups.push({ dayLabel: getDayLabel(r.login_at), dayKey, rows: [r] });
      } else {
        groups[groups.length - 1].rows.push(r);
      }
    });
    return groups;
  }, [paginated]);

  return (
    <div className="space-y-4">
      {/* LGPD notice */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-violet-500/10 border border-violet-500/20 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-violet-400 text-[14px]" />
        </div>
        <p className="text-[12px] text-violet-300 leading-relaxed">
          <strong>LGPD — Acesso restrito.</strong>{' '}
          IPs parcialmente mascarados (últimos octetos ocultos). Dados disponíveis apenas para administradores autorizados.
        </p>
      </div>

      {/* Legend for synthetic entries */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-information-line text-amber-400 text-[14px]" />
        </div>
        <p className="text-[12px] text-amber-300/80 leading-relaxed">
          <strong>Cobertura de dados:</strong>{' '}
          Entradas com fundo âmbar indicam usuários cujo registro de sessão não foi capturado (ex: acessaram antes da feature de sessões ser ativada). O horário exibido é o <em>último acesso registrado</em> via perfil — sem dados de dispositivo/navegador.
        </p>
      </div>

      {/* Quick date filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {QUICK_FILTERS.map((qf) => (
          <button
            key={qf.id}
            onClick={() => { setQuickFilter(qf.id); resetPage(); }}
            className={`px-4 py-2 rounded-xl text-[13px] font-500 whitespace-nowrap cursor-pointer transition-all border ${
              quickFilter === qf.id
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30'
                : 'text-white/40 hover:bg-white/5 hover:text-white border-transparent'
            }`}
          >
            {qf.label}
          </button>
        ))}
        <div className="ml-auto text-[12px] text-white/30 whitespace-nowrap">
          {filtered.length} registro{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Search + device + browser filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1 min-w-[180px] max-w-sm">
          <i className="ri-search-line text-white/30 text-[14px]" />
          <input
            className="bg-transparent text-white text-[13px] outline-none placeholder:text-white/30 w-full"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
          />
          {search && (
            <button
              onClick={() => { setSearch(''); resetPage(); }}
              className="text-white/30 hover:text-white/60 cursor-pointer"
            >
              <i className="ri-close-line text-[14px]" />
            </button>
          )}
        </div>

        <select
          value={deviceFilter}
          onChange={(e) => { setDeviceFilter(e.target.value); resetPage(); }}
          className="bg-white/5 border border-white/10 text-white text-[13px] rounded-xl px-3 py-2 cursor-pointer outline-none"
        >
          <option value="" className="bg-[#1a1a2e]">Todos os dispositivos</option>
          <option value="desktop" className="bg-[#1a1a2e]">Desktop</option>
          <option value="mobile" className="bg-[#1a1a2e]">Mobile</option>
          <option value="tablet" className="bg-[#1a1a2e]">Tablet</option>
        </select>

        {browsers.length > 0 && (
          <select
            value={browserFilter}
            onChange={(e) => { setBrowserFilter(e.target.value); resetPage(); }}
            className="bg-white/5 border border-white/10 text-white text-[13px] rounded-xl px-3 py-2 cursor-pointer outline-none"
          >
            <option value="" className="bg-[#1a1a2e]">Todos os navegadores</option>
            {browsers.map((b) => (
              <option key={b} value={b} className="bg-[#1a1a2e]">{b}</option>
            ))}
          </select>
        )}

        {(search || deviceFilter || browserFilter) && (
          <button
            onClick={() => { setSearch(''); setDeviceFilter(''); setBrowserFilter(''); resetPage(); }}
            className="flex items-center gap-1.5 px-3 py-2 text-[12px] text-white/40 hover:text-white bg-white/5 border border-white/10 rounded-xl cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-close-line" />
            Limpar filtros
          </button>
        )}
      </div>

      {/* Table */}
      {groupedRows.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl flex flex-col items-center justify-center py-16 gap-3">
          <i className="ri-login-box-line text-4xl text-white/15" />
          <p className="text-white/30 text-[13px]">Nenhum login encontrado com esses filtros</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupedRows.map((group) => (
            <div key={group.dayKey}>
              {/* Day header */}
              <div className="flex items-center gap-3 mb-2 px-1">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-event-line text-[13px] text-white/30" />
                  <span className="text-[12px] font-600 text-white/50 capitalize">{group.dayLabel}</span>
                </div>
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[11px] text-white/25">
                  {group.rows.length} login{group.rows.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Rows for this day */}
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px]">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Horário
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Usuário
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Plano
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Navegador
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Dispositivo / SO
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          IP
                        </th>
                        <th className="text-left px-4 py-2.5 text-[10px] font-600 text-white/30 uppercase tracking-wider">
                          Último ativo
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((row, idx) => {
                        const loginDt = fmtDateTime(row.login_at);
                        const lastDt = fmtDateTime(row.last_active_at);
                        const deviceIcon = DEVICE_ICONS[row.device_type ?? ''] ?? 'ri-question-line';
                        const deviceColor = DEVICE_COLORS[row.device_type ?? ''] ?? 'text-white/30';
                        const browserIcon = getBrowserIcon(row.browser_name);
                        const ac = avatarColor(row.full_name || row.email || '?');
                        const initial = (row.full_name || row.email || '?')[0].toUpperCase();

                        return (
                          <tr
                            key={row.session_id}
                            className={`border-b border-white/5 transition-colors ${
                              idx === group.rows.length - 1 ? 'border-b-0' : ''
                            } ${
                              row.is_synthetic
                                ? 'hover:bg-amber-500/[0.04] bg-amber-500/[0.02]'
                                : 'hover:bg-white/[0.03]'
                            }`}
                          >
                            {/* Horário */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.is_synthetic ? 'bg-amber-400/60' : 'bg-cyan-400/60'}`} />
                                <div>
                                  <p className="text-[13px] font-600 text-white tabular-nums">
                                    {loginDt.time || '—'}
                                  </p>
                                  <p className="text-[11px] text-white/30">{loginDt.date}</p>
                                  {row.is_synthetic && (
                                    <p className="text-[10px] text-amber-400/70 mt-0.5">último acesso</p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Usuário */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${ac}`}>
                                  <span className="text-[11px] font-700">{initial}</span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[13px] text-white font-500 leading-tight truncate max-w-[150px]">
                                    {row.full_name || <span className="italic text-white/30">Sem nome</span>}
                                  </p>
                                  <p className="text-[11px] text-white/30 truncate max-w-[150px]">
                                    {row.email}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Plano */}
                            <td className="px-4 py-3">
                              {row.is_admin ? (
                                <span className="text-[11px] font-600 text-amber-300 bg-amber-500/15 border border-amber-500/25 px-2 py-0.5 rounded-md whitespace-nowrap">
                                  Admin
                                </span>
                              ) : (
                                <span className="text-[11px] font-500 text-white/50 bg-white/5 px-2 py-0.5 rounded-md whitespace-nowrap">
                                  {row.plan_name}
                                </span>
                              )}
                            </td>

                            {/* Navegador */}
                            <td className="px-4 py-3">
                              {row.is_synthetic ? (
                                <span className="text-[11px] text-amber-400/60 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md whitespace-nowrap">
                                  Sem dados
                                </span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                    <i className={`${browserIcon} text-[15px] text-white/50`} />
                                  </div>
                                  <span className="text-[12px] text-white/60 whitespace-nowrap">
                                    {row.browser_name ?? '—'}
                                  </span>
                                </div>
                              )}
                            </td>

                            {/* Dispositivo / SO */}
                            <td className="px-4 py-3">
                              {row.is_synthetic ? (
                                <span className="text-[12px] text-white/20">—</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                    <i className={`${deviceIcon} text-[15px] ${deviceColor}`} />
                                  </div>
                                  <div>
                                    <p className="text-[12px] text-white/60 capitalize whitespace-nowrap">
                                      {row.device_type ?? '—'}
                                    </p>
                                    {row.os_name && (
                                      <p className="text-[11px] text-white/30 whitespace-nowrap">
                                        {row.os_name}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* IP */}
                            <td className="px-4 py-3">
                              {row.ip_address ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                                    <i className="ri-map-pin-2-line text-[12px] text-white/25" />
                                  </div>
                                  <span className="text-[12px] text-white/40 font-mono">
                                    {row.ip_address}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[12px] text-white/20">—</span>
                              )}
                            </td>

                            {/* Último ativo */}
                            <td className="px-4 py-3">
                              {row.last_active_at ? (
                                <div>
                                  <p className="text-[12px] text-white/50 tabular-nums whitespace-nowrap">
                                    {lastDt.time}
                                  </p>
                                  <p className="text-[11px] text-white/25 whitespace-nowrap">
                                    {lastDt.date}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-[12px] text-white/20">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-white/30">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de{' '}
            {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-s-line text-[16px]" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const pageNum =
                totalPages <= 7
                  ? i + 1
                  : page <= 4
                    ? i + 1
                    : page >= totalPages - 3
                      ? totalPages - 6 + i
                      : page - 3 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 flex items-center justify-center rounded-xl text-[13px] font-600 cursor-pointer transition-colors ${
                    page === pageNum
                      ? 'bg-cyan-600 text-white'
                      : 'bg-white/5 border border-white/10 text-white/40 hover:text-white'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-s-line text-[16px]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
